/**
 * useEntityAlerts — DESIGN_CONTRACT §7 + Wave 19 #84.
 *
 * Returns the count of UNREAD directed notifications the current user has
 * about each entity of `entityType` ("jobcard" | "ticket" | etc).
 *
 * Pairs with the existing `useUnseen` hook:
 *   - useUnseen     → "this row is new since I last opened the list"
 *   - useEntityAlerts → "this row has something pending FOR ME — comments,
 *                       assignments, photos, status changes since I last
 *                       opened that entity's detail page"
 *
 * Polls every 15s while tab is foreground (matches the priority banner
 * heartbeat).
 *
 * Returns:
 *   {
 *     unreadFor(id)   : number  — count of unread alerts for entity id
 *     hasAlerts(id)   : boolean — convenience for unreadFor(id) > 0
 *     reload()        : () => Promise<void>
 *     loaded          : boolean
 *     totalUnread     : number  — sum across all entities of this type
 *   }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

const POLL_MS = 15_000;

export default function useEntityAlerts(entityType) {
  const [counts, setCounts] = useState({});
  const [loaded, setLoaded] = useState(false);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    if (!entityType) return;
    try {
      const { data } = await api.get(
        `/notifications/entity-unread?entity_type=${encodeURIComponent(entityType)}`,
      );
      if (!cancelledRef.current) {
        setCounts(data?.counts || {});
      }
    } catch (_e) {
      // Fail silent — the row badges are non-critical.
    } finally {
      if (!cancelledRef.current) setLoaded(true);
    }
  }, [entityType]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  const unreadFor = useCallback((id) => (id ? (counts[id] || 0) : 0), [counts]);
  const hasAlerts = useCallback((id) => unreadFor(id) > 0, [unreadFor]);
  const totalUnread = Object.values(counts).reduce((a, b) => a + b, 0);

  return { unreadFor, hasAlerts, reload: load, loaded, totalUnread };
}
